﻿import { formatFileWithLineNumbers, resolveUserPath } from "../utils/fs.js";
import { ToolExecutionError } from "../tools/core/errors.js";
import { inspectTextFile } from "../tools/core/fileIntrospection.js";
import { findPathSuggestions } from "../tools/core/pathSuggestions.js";
import { toToolRelativePath } from "../tools/core/pathDisplay.js";
import { okResult, parseArgs, readOptionalNumber, readString } from "../tools/core/shared.js";
import type { RegisteredTool } from "../tools/core/types.js";

interface ReadWindow {
  start: number;
  endExclusive: number;
  requestedLimit?: number;
}

export const readToolDefinition: RegisteredTool = {
  definition: {
    type: "function",
    function: {
      name: "read",
      description: "Read a local text file. Returns numbered lines and a continuation pointer. Use path/offset/limit only; offset is a 1-based line number.",
      parameters: {
        type: "object",
        properties: {
          path: {
            type: "string",
            description: "Path to the file to read.",
          },
          offset: {
            type: "number",
            description: "1-based line number to start reading from. Optional; defaults to 1.",
          },
          limit: {
            type: "number",
            description: "Maximum number of lines to return from offset. Optional.",
          },
        },
        required: ["path"],
        additionalProperties: false,
      },
    },
  },
  async execute(rawArgs, context) {
    const args = parseArgs(rawArgs);
    const targetPath = readString(args.path, "path");
    const offset = readOptionalNumber(args.offset);
    const limit = readOptionalNumber(args.limit);
    const resolved = resolveUserPath(targetPath, context.cwd);
    const displayPath = toToolRelativePath(context.cwd, resolved);

    if (offset !== undefined && offset < 1) {
      throw new Error("read offset must be a 1-based line number.");
    }

    let inspected;
    try {
      inspected = await inspectTextFile(resolved, context.config.maxReadBytes);
    } catch (error) {
      const errno = error as NodeJS.ErrnoException;
      if (errno.code === "ENOENT") {
        const suggestions = await findPathSuggestions(context.cwd, targetPath, context.projectContext);
        throw new ToolExecutionError(`File not found: ${targetPath}`, {
          code: "ENOENT",
          details: {
            requestedPath: targetPath,
            suggestions,
          },
        });
      }

      throw error;
    }

    if (!inspected.readable) {
      return okResult(
        JSON.stringify(
          {
            path: displayPath,
            absolutePath: resolved,
            readable: false,
            reason: inspected.reason,
            size: inspected.size,
            extension: inspected.extension,
            capabilityHintCode: inspected.capabilityHintCode,
            presentation: inspected.presentation ?? "metadata_only",
            detectedCapability: inspected.detectedCapability,
          },
          null,
          2,
        ),
      );
    }

    const lines = (inspected.content ?? "").split(/\r?\n/);
    const readWindow = resolveReadWindow(lines.length, offset, limit);
    if (readWindow.start >= lines.length && !(lines.length === 1 && lines[0] === "" && readWindow.start === 0)) {
      throw new Error(`read offset ${offset ?? 1} is beyond end of file (${lines.length} lines total).`);
    }

    const fittedEndExclusive = fitWindowWithinBudget(lines, readWindow.start, readWindow.endExclusive, context.config.maxReadBytes);
    const selected = lines.slice(readWindow.start, fittedEndExclusive).join("\n");
    const content = formatFileWithLineNumbers(selected, readWindow.start + 1);
    const hasMore = fittedEndExclusive < lines.length;
    await context.recordWorksetFile?.({
      path: resolved,
      toolName: "read",
      changed: false,
      reason: hasMore ? "partial read" : "read",
    });

    return okResult(
      JSON.stringify(
        {
          path: displayPath,
          absolutePath: resolved,
          readable: true,
          size: inspected.size,
          extension: inspected.extension,
          startLine: readWindow.start + 1,
          endLine: fittedEndExclusive === 0 ? 0 : fittedEndExclusive,
          truncated: hasMore,
          content,
          continuation: hasMore
            ? {
                hasMore: true,
                nextOffset: fittedEndExclusive + 1,
                limit: readWindow.requestedLimit ?? Math.max(1, fittedEndExclusive - readWindow.start),
                remainingLines: lines.length - fittedEndExclusive,
                continuationArgs: {
                  path: displayPath,
                  offset: fittedEndExclusive + 1,
                  limit: readWindow.requestedLimit ?? Math.max(1, fittedEndExclusive - readWindow.start),
                },
              }
            : undefined,
        },
        null,
        2,
      ),
    );
  },
};

function resolveReadWindow(totalLines: number, offset: number | undefined, limit: number | undefined): ReadWindow {
  const start = Math.max(0, (offset ?? 1) - 1);
  const requestedLimit = limit === undefined ? undefined : Math.max(1, limit || 1);
  return {
    start,
    endExclusive: requestedLimit === undefined ? totalLines : Math.min(totalLines, start + requestedLimit),
    requestedLimit,
  };
}

function fitWindowWithinBudget(lines: string[], start: number, requestedEndExclusive: number, maxChars: number): number {
  if (start >= lines.length) {
    return lines.length;
  }

  let endExclusive = Math.max(start + 1, requestedEndExclusive);
  while (endExclusive > start + 1) {
    const content = formatFileWithLineNumbers(lines.slice(start, endExclusive).join("\n"), start + 1);
    if (content.length <= maxChars) {
      return endExclusive;
    }

    endExclusive -= 1;
  }

  return Math.min(lines.length, start + 1);
}
